import { Snackbar, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { getFullLicense } from 'API/license';
import { patchLinkedinAccountLimits, patchLinkedinAccountWorktime } from 'API/linkedin-account-limits';
import {
  deleteLinkedinAccount,
  getLinkedinAccountById,
  getShareableToken,
  logoutLinkedinAccount,
  resetLinkedinAccount,
  resetLinkedinAccountLocalStorage,
  updateLinkedinAccountEmailCredentials,
  updateLinkedinAccountNames,
  updateLinkedinAccountSignature,
  updateLinkedinAccountWithPayload,
} from 'API/linkedin-accounts';
import { getFunnelsOfLinkedinAccount, LimitWeights, updateLinkedinFunnelLimitWeights } from 'API/linkedin-funnel-limit-weights';
import { LinkedinTypes, ProductGroups, Products, Roles, Routes, ZoomLevels } from 'enums';
import CustomerOnboarding from 'features/onboarding/general-onboarding-journey/customer-onboarding';
import PaiLink from 'features/ui/base-components/pai-link';
import useAppSync from 'hooks/use-app-sync';
import { useHasLicense } from 'hooks/use-has-license';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import Logger from 'logger';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useHref, useNavigate, useSearchParams } from 'react-router';
import useCoPilotOnboardingStore from 'stores/co-pilot-onboarding-store';
import useErrorStore from 'stores/error-store';
import useUserStore from 'stores/user-store';
import { makeStyles } from 'tss-react/mui';
import { normalizeWorkHours, toggleWorkHour } from '../campaign-builder/campaign-building-panel/campaign-settings/work-blocks';
import { OptionType } from '../../ui/base-components/pai-auto-complete';
import PaiDotLoader from '../../ui/base-components/pai-dot-loader';
import AccountAlreadyExistsDialog from './account-already-exists-dialog';
import AccountAlreadySyncedDialog from './account-already-synced-dialog';
import AssignLicenseDialog from './assign-license-dialog';
import AddAccountButton from './add-account-button';
import AppSyncDialog from './app-sync-dialog/app-sync-dialog';
import EditAccountLimitsAndWeightsDialog from './edit-account-limits-and-weights-dialog';
import EditEmailCredentialsDialog from './edit-email-credentials-dialog';
import EditLinkedinAccountGeneralSettingsDialog from './edit-linkedin-account-general-settings-dialog';
import EditLinkedinAccountLoginInformationDialog from './edit-linkedin-account-login-information-dialog';
import EditLinkedinAccountNameDialog from './edit-linkedin-account-name-dialog';
import EditSignatureDialog from './edit-signature-dialog';
import FairUsePauseDialog from './fair-use-pause-dialog';
import LinkedinAccountItem from './linkedin-account-item';
import {
  EmailAccountTypes,
  EmailCredentialErrors,
  EmailCredentials,
  Encryptions,
  LinkedinAccount,
  LinkedinAccountStatuses,
  OAuthCredentials,
} from './linkedin-accounts.types';
import { useLinkedinOverviewDialogs } from './hooks/use-linkedin-overview-dialogs';
import ManageEmailSendersDialog from './manage-email-senders-dialog';
import OnboardingIncompleteDialog from './onboarding-incomplete-dialog';
import RecruiterParallelLoginDialog from './recruiter-parallel-login-dialog';
import RemoveLinkedinAccountDialog from './remove-linkedin-account-dialog';
import ResetLocalStorageDialog from './reset-local-storage-dialog';
import AccountConnectionsDialog from './account-connections-dialog';
import TwoFaNotEnabledDialog from './two-fa-not-enabled-dialog';
import WaitBeforeSyncDialog from './wait-before-sync-dialog';

interface Props {
  productGroup: ProductGroups;
}

const LinkedinAccountOverview: React.FC<Props> = ({ productGroup }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { token, role, onboardingDone } = useUserData();
  const navigate = useNavigate();
  const setUserLicenses = useUserStore((s) => s.setLicenses);
  const userLicenses = useUserStore((s) => s.licenses || []);

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);
  const handleOpenCoPilotOnboardingDialog = useCoPilotOnboardingStore((state) => state.handleOpenCoPilotOnboardingDialog);

  const {
    // fetch
    fetchLinkedinAccounts,
    setLinkedinAccounts,
    // app sync dialog props
    appSyncDialog,
    allCountryCodes,
    linkedinAccounts,
    linkedinAccountsLoading,
    licenseCapacity,
    linkedLicensesMap,
    handleOpenAppSyncDialog,
    handleOpenAppSyncAndSynchronizeLiAccount,
    handleOpenAppSyncForLogout,
    handleOpenAppSyncAndVerifyPin,
    handleOpenAppSyncThirdPartyWebsite,
    handleOpenAppSyncAndSolveCaptcha,
    handleOpenAppSyncAndCorrectAccessData,
    handleCloseAppSyncDialog,
    increaseActiveStepIndex,
    decreaseActiveStepIndex,
    handleAppSyncLinkedinProductChange,
    handleAppSyncCountryChange,
    handleAppSyncCityChange,
    handleAppSyncUserNameChange,
    handleAppSyncPasswordChange,
    handleAppSyncLicenseSelect,
    handleCreateAndSynchronizeLiAccount,
    handleAppSyncVerificationCodeChange,
    handleVerifyLinkedinAccountFromAppSync,
    handleImageClickFromAppSync,
    handleDeleteMarkerFromAppSync,
    handleSolveCaptchaFromAppSync,
    handleSelectContractFromAppSync,
    handleAddContractFromAppSync,
    handleEditAndSynchronizeLiAccount,
    handleReSynchronizeLiAccount,
    handleResetLinkedinAccount,
    handleThirdPartyClickFromAppSync,
    handleThirdPartyTextChange,
    handleSetThirdPartyTextFromAppSync,
    // other props
    accountAlreadyExistsDialog,
    handleCloseAccountAlreadyExistsDialog,
    waitBeforeSyncDialog,
    handleCloseWaitBeforeSyncDialog,
    accountAlreadySyncedDialog,
    handleCloseAccountAlreadySyncedDialog,
  } = useAppSync();

  // All dialog states consolidated into a single hook
  const {
    removeLinkedinAccountDialog,
    setRemoveLinkedinAccountDialog,
    resetRemoveLinkedinAccountDialog,
    editLinkedinAccountGeneralSettingsDialog,
    setEditLinkedinAccountGeneralSettingsDialog,
    resetEditLinkedinAccountGeneralSettingsDialog,
    editLinkedinAccountLoginInformationDialog,
    setEditLinkedinAccountLoginInformationDialog,
    resetEditLinkedinAccountLoginInformationDialog,
    editEmailCredentialsDialog,
    setEditEmailCredentialsDialog,
    editLinkedinAccountNameDialog,
    setEditLinkedinAccountNameDialog,
    resetEditLinkedinAccountNameDialog,
    recruiterParallelLoginDialog,
    setRecruiterParallelLoginDialog,
    resetRecruiterParallelLoginDialog,
    onboardingIncompleteDialog,
    setOnboardingIncompleteDialog,
    resetOnboardingIncompleteDialog,
    fairUsePauseDialog,
    setFairUsePauseDialog,
    twoFaNotEnabledDialog,
    setTwoFaNotEnabledDialog,
    manageEmailSendersDialog,
    setManageEmailSendersDialog,
    editLinkedinAccountLimitsDialog,
    setEditLinkedinAccountLimitsDialog,
    resetEditLinkedinAccountLimitsDialog,
    resetLocalStorageDialog,
    setResetLocalStorageDialog,
    resetResetLocalStorageDialog,
    accountConnectionsDialog,
    setAccountConnectionsDialog,
    resetAccountConnectionsDialog,
    quotaSplitDialog,
    setQuotaSplitDialog,
    resetQuotaSplitDialog,
    editSignatureDialog,
    setEditSignatureDialog,
    resetEditSignatureDialog,
  } = useLinkedinOverviewDialogs();

  const [assignLicenseDialog, setAssignLicenseDialog] = useState<{ open: boolean; accountId: string }>({
    open: false,
    accountId: '',
  });

  const [logoutLoading, setLogoutLoading] = useState<string[]>([]);
  const [generateCreationLinkLoading, setGenerateCreationLinkLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const pathname = generatePath(`${Routes.EXTERNAL_LINKEDIN_ACCOUNTS}`);
  const path = useHref(pathname);

  // Deep link from the manual-login alert email: `?relogin=<accountId>` auto-opens
  // the re-login flow (2FA popup) for that account as soon as the account list has
  // loaded — so the recipient only has to click the button in the email. Fires once;
  // does nothing if the account has meanwhile recovered (only acts on NEEDS_MANUAL_LOGIN).
  const [searchParams, setSearchParams] = useSearchParams();
  const reloginHandledRef = useRef(false);
  useEffect(() => {
    if (reloginHandledRef.current) return;
    const reloginId = searchParams.get('relogin');
    if (!reloginId) return;
    if (linkedinAccountsLoading || _.isEmpty(linkedinAccounts)) return; // wait for the list to load
    reloginHandledRef.current = true;
    const account = linkedinAccounts.find((a) => a._id === reloginId);
    if (account && account.status === LinkedinAccountStatuses.NEEDS_MANUAL_LOGIN) {
      handleOpenAppSyncAndSynchronizeLiAccount(account)();
    }
    // Clear the param so a refresh / re-render doesn't re-trigger the login.
    searchParams.delete('relogin');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, linkedinAccounts, linkedinAccountsLoading, handleOpenAppSyncAndSynchronizeLiAccount]);

  const handleOpenUpgradePage = () => {
    const product = productGroup === ProductGroups.HR_BOOSTER ? 'hr-booster' : 'lead-booster';
    navigate(`${Routes.UPGRADE}?product=${product}`);
  };

  // License status aligned with Step 4 (Campaign Settings)
  const productLicensed = useHasLicense(
    productGroup === ProductGroups.HR_BOOSTER ? Products.SAIA_RECRUITING : Products.SAIA_SALES
  );
  const isUnlicensed = !productLicensed;

  const moveToCampaignBuilder = () => {
    handleCloseAppSyncDialog();
    navigate(productGroup === ProductGroups.HR_BOOSTER ? Routes.RECRUITING_BOOSTER_CO_PILOT_CAMPAIGN : Routes.LEAD_BOOSTER_CO_PILOT_CAMPAIGN, {
      replace: true,
    });
    handleOpenCoPilotOnboardingDialog(onboardingDone);
  };

  /**
   * Remove linkedin account dialog handlers
   */

  const handleOpenRemoveLinkedinAccountDialog = (account: LinkedinAccount) => {
    return () => {
      setRemoveLinkedinAccountDialog((prev) => ({ ...prev, open: true, account }));
    };
  };

  const handleCloseRemoveLinkedinAccountDialog = () => {
    resetRemoveLinkedinAccountDialog();
  };

  const handleRemoveLinkedinAccount = async () => {
    if (!_.isNil(removeLinkedinAccountDialog.account)) {
      setRemoveLinkedinAccountDialog((prev) => ({ ...prev, loading: true }));

      const res = await deleteLinkedinAccount(removeLinkedinAccountDialog.account?._id);

      if (!res.error) {
        fetchLinkedinAccounts(true);
        handleCloseRemoveLinkedinAccountDialog();
      } else {
        setRemoveLinkedinAccountDialog((prev) => ({ ...prev, loading: false }));
        handleOpenErrorDialog(res.error, '/linkedinAccounts/:id', 'delete');
      }
    }
  };

  /**
   * Edit linkedin account general settings dialog handlers
   */

  const handleOpenEditLinkedinAccountGeneralSettingsDialog = (account: LinkedinAccount) => {
    const foundCountry = allCountryCodes.find((country) => country.code === account.countryName);
    let userCountry = { label: '', value: '' };
    let userCity = { label: '', value: '' };

    if (!_.isNil(foundCountry)) {
      userCountry = { label: foundCountry.name, value: foundCountry.code };

      const foundCity = foundCountry.cities.options.find((city) => city.code === account.cityName);

      if (!_.isNil(foundCity)) {
        userCity = { label: foundCity.name, value: foundCity.code };
      }
    }

    return () => {
      setEditLinkedinAccountGeneralSettingsDialog((prev) => ({
        ...prev,
        open: true,
        account,
        selectedCountry: userCountry,
        selectedCity: userCity,
        contractName: account.contractName ? account.contractName : '',
        linkedinProduct: account.liProductType,
      }));
    };
  };

  const handleCloseEditLinkedinAccountGeneralSettingsDialog = () => {
    resetEditLinkedinAccountGeneralSettingsDialog();
  };

  const handleEditCountryChange = (event: React.ChangeEvent<any>, newValue: OptionType) => {
    setEditLinkedinAccountGeneralSettingsDialog((prev) => ({ ...prev, selectedCountry: newValue, selectedCity: { label: '', value: '' } }));
  };

  const handleEditCityChange = (event: React.ChangeEvent<any>, newValue: OptionType) => {
    setEditLinkedinAccountGeneralSettingsDialog((prev) => ({ ...prev, selectedCity: newValue }));
  };

  const handleEditLinkedinProductChange = (value: LinkedinTypes) => {
    return () => {
      setEditLinkedinAccountGeneralSettingsDialog((prev) => ({ ...prev, linkedinProduct: value }));
    };
  };

  const handleEditLinkedinAccountGeneralSettings = async () => {
    if (!_.isNil(editLinkedinAccountGeneralSettingsDialog.account)) {
      setEditLinkedinAccountGeneralSettingsDialog((prev) => ({ ...prev, loading: true }));

      const payload = {
        liProductType: editLinkedinAccountGeneralSettingsDialog.linkedinProduct,
        countryName: editLinkedinAccountGeneralSettingsDialog.selectedCountry.value,
        cityName: editLinkedinAccountGeneralSettingsDialog.selectedCity.value,
      };

      const res = await updateLinkedinAccountWithPayload(editLinkedinAccountGeneralSettingsDialog.account._id, payload);

      if (!res.error) {
        fetchLinkedinAccounts(true);
        handleCloseEditLinkedinAccountGeneralSettingsDialog();
      } else {
        setEditLinkedinAccountGeneralSettingsDialog((prev) => ({ ...prev, loading: false }));
        handleOpenErrorDialog(res.error, '/linkedinAccounts/:id', 'put');
      }
    }
  };

  /**
   * Edit linkedin account login information dialog handlers
   */

  const handleOpenEditLinkedinAccountLoginInformationDialog = (account: LinkedinAccount) => {
    return () => {
      setEditLinkedinAccountLoginInformationDialog((prev) => ({
        ...prev,
        open: true,
        account,
        userName: account.userEmail,
        password: '',
      }));
    };
  };

  const handleCloseEditLinkedinAccountLoginInformationDialog = () => {
    resetEditLinkedinAccountLoginInformationDialog();
  };

  const handleEditUserNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditLinkedinAccountLoginInformationDialog((prev) => ({ ...prev, userName: event.target.value }));
  };

  const handleEditPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditLinkedinAccountLoginInformationDialog((prev) => ({ ...prev, password: event.target.value }));
  };

  const handleEditLinkedinAccountLoginInformation = async () => {
    if (!_.isNil(editLinkedinAccountLoginInformationDialog.account)) {
      setEditLinkedinAccountLoginInformationDialog((prev) => ({ ...prev, loading: true }));

      const payload = {
        userEmail: editLinkedinAccountLoginInformationDialog.userName,
        userPassword: editLinkedinAccountLoginInformationDialog.password,
      };

      const res = await updateLinkedinAccountWithPayload(editLinkedinAccountLoginInformationDialog.account._id, payload);

      if (!res.error) {
        fetchLinkedinAccounts(true);
        handleCloseEditLinkedinAccountLoginInformationDialog();
      } else {
        setEditLinkedinAccountLoginInformationDialog((prev) => ({ ...prev, loading: false }));
        handleOpenErrorDialog(res.error, '/linkedinAccounts/:id', 'put');
      }
    }
  };

  /**
   * Edit email credentials dialog handlers
   */

  const handleEmailAccountTypeChange = (value: string) => {
    return () => {
      setEditEmailCredentialsDialog((prev) => ({ ...prev, type: value as EmailAccountTypes }));
    };
  };

  const handleOpenEditEmailCredentialsDialog = (account: LinkedinAccount) => {
    return () => {
      setEditEmailCredentialsDialog((prev) => ({
        ...prev,
        open: true,
        account,
        accessRevoked: !!account.emailConfig?.accessRevoked,
        type: !_.isNil(account.emailConfig?.smtp)
          ? EmailAccountTypes.OTHER
          : !_.isNil(account.emailConfig?.oauth)
          ? EmailAccountTypes.MS_O365
          : !_.isNil(account.emailConfig?.gmailOauth)
          ? EmailAccountTypes.GMAIL
          : EmailAccountTypes.OTHER,
        smtp: account.emailConfig?.smtp
          ? { ...account.emailConfig.smtp, password: '' }
          : {
              server: '',
              port: '',
              encryption: Encryptions.NONE,
              username: '',
              password: '',
            },
        imap: account.emailConfig?.imap
          ? { ...account.emailConfig.imap, password: '' }
          : {
              server: '',
              port: '',
              encryption: Encryptions.NONE,
              username: '',
              password: '',
            },
        oauth: account.emailConfig?.oauth
          ? { ...account.emailConfig.oauth, code: '' }
          : account.emailConfig?.gmailOauth
          ? { ...account.emailConfig.gmailOauth, code: '' }
          : {
              email: '',
              code: '',
            },
      }));
    };
  };

  const handleCloseEditEmailCredentialsDialog = () => {
    setEditEmailCredentialsDialog((prev) => ({
      ...prev,
      open: false,
      loading: false,
    }));
  };

  // Ensure licenses are loaded when coming in via top nav (Accounts)
  useEffect(() => {
    (async () => {
      if (!userLicenses || userLicenses.length === 0) {
        const res = await getFullLicense();
        if (Array.isArray(res)) {
          setUserLicenses(res);
        }
      }
    })();
  }, [token]);

  useEffect(() => {
    if (!editEmailCredentialsDialog.open) {
      setTimeout(() => {
        setEditEmailCredentialsDialog((prev) => ({
          ...prev,
          type: EmailAccountTypes.OTHER,
          smtp: {
            server: '',
            port: '',
            encryption: Encryptions.NONE,
            username: '',
            password: '',
          },
          imap: {
            server: '',
            port: '',
            encryption: Encryptions.NONE,
            username: '',
            password: '',
          },
          oauth: {
            email: '',
            code: '',
          },
          account: null,
          error: undefined,
          accessRevoked: false,
        }));
      }, 225);
    }
  }, [editEmailCredentialsDialog.open]);

  const handleSmtpEmailCredentialsChange = (field: keyof EmailCredentials) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setEditEmailCredentialsDialog((prev) => ({
        ...prev,
        smtp: {
          ...prev.smtp,
          [field]: event.target.value,
        },
      }));
    };
  };

  const handleSmtpEncryptionChange = (value: string) => {
    return () => {
      setEditEmailCredentialsDialog((prev) => ({
        ...prev,
        smtp: {
          ...prev.smtp,
          encryption: value as Encryptions,
        },
      }));
    };
  };

  const handleImapEmailCredentialsChange = (field: keyof EmailCredentials) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setEditEmailCredentialsDialog((prev) => ({
        ...prev,
        imap: {
          ...prev.imap,
          [field]: event.target.value,
        },
      }));
    };
  };

  const handleImapEncryptionChange = (value: string) => {
    return () => {
      setEditEmailCredentialsDialog((prev) => ({
        ...prev,
        imap: {
          ...prev.imap,
          encryption: value as Encryptions,
        },
      }));
    };
  };

  const handleOAuthEmailCredentialsChange = (field: keyof OAuthCredentials) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      setEditEmailCredentialsDialog((prev) => ({
        ...prev,
        oauth: {
          ...prev.oauth,
          [field]: event.target.value,
        },
      }));
    };
  };

  const handleAcceptEmailCredentials = async () => {
    if (!_.isNil(editEmailCredentialsDialog.account)) {
      setEditEmailCredentialsDialog((prev) => ({ ...prev, loading: true, error: undefined }));

      const account = editEmailCredentialsDialog.account;
      const res =
        editEmailCredentialsDialog.type === EmailAccountTypes.MS_O365 || editEmailCredentialsDialog.type === EmailAccountTypes.GMAIL
          ? await updateLinkedinAccountEmailCredentials(
              account._id,
              editEmailCredentialsDialog.type,
              undefined,
              undefined,
              editEmailCredentialsDialog.oauth
            )
          : await updateLinkedinAccountEmailCredentials(
              account._id,
              editEmailCredentialsDialog.type,
              editEmailCredentialsDialog.smtp,
              editEmailCredentialsDialog.imap,
              undefined
            );

      if (!res.error) {
        fetchLinkedinAccounts(true);
        handleCloseEditEmailCredentialsDialog();
      } else {
        if (res.error.code === 400 && res.error.message?.includes('Server details are incorrect. Please check your SMTP settings.')) {
          setEditEmailCredentialsDialog((prev) => ({ ...prev, loading: false, error: EmailCredentialErrors.SMTP }));
        } else if (res.error.code === 400 && res.error.message?.includes('Server details are incorrect. Please check your IMAP settings.')) {
          setEditEmailCredentialsDialog((prev) => ({ ...prev, loading: false, error: EmailCredentialErrors.IMAP }));
        } else if (res.error.code === 400 && res.error.message?.includes('User credentials are incorrect.')) {
          setEditEmailCredentialsDialog((prev) => ({ ...prev, loading: false, error: EmailCredentialErrors.CREDENTIALS }));
        } else {
          handleOpenErrorDialog(res.error, '/linkedinAccounts/:id/saveEmailConfig', 'post');
        }

        setEditEmailCredentialsDialog((prev) => ({ ...prev, loading: false }));
      }
    }
  };

  /**
   * Edit linkedin account name dialog handlers
   */

  const handleOpenEditLinkedinAccountNameDialog = (account: LinkedinAccount) => {
    return () => {
      const { firstName, lastName } = account;
      setEditLinkedinAccountNameDialog((prev) => ({ ...prev, open: true, account, firstName: firstName || '', lastName: lastName || '' }));
    };
  };

  const handleFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditLinkedinAccountNameDialog((prev) => ({ ...prev, firstName: event.target.value }));
  };

  const handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditLinkedinAccountNameDialog((prev) => ({ ...prev, lastName: event.target.value }));
  };

  const handleCloseEditLinkedinAccountNameDialog = () => {
    resetEditLinkedinAccountNameDialog();
  };

  const handleEditLinkedinAccountName = async () => {
    const { account, firstName, lastName } = editLinkedinAccountNameDialog;

    if (_.isNil(account)) return;

    setEditLinkedinAccountNameDialog((prev) => ({ ...prev, loading: true }));

    const linkedinAccountId = account._id;

    const res = await updateLinkedinAccountNames(linkedinAccountId, firstName || undefined, lastName || undefined);

    if (!res.error) {
      const updateAccounts = _.cloneDeep(linkedinAccounts);
      const findLinkedinAccount = updateAccounts.find((account) => account._id === linkedinAccountId);

      if (!_.isNil(findLinkedinAccount)) {
        if (firstName) {
          findLinkedinAccount.firstName = firstName;
        }

        if (lastName) {
          findLinkedinAccount.lastName = lastName;
        }
      }

      setLinkedinAccounts(updateAccounts);
      handleCloseEditLinkedinAccountNameDialog();
    } else {
      setEditLinkedinAccountNameDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res.error, '/linkedinAccounts/:id', 'put');
    }
  };

  /**
   * Recruiter parallel login dialog handlers
   */

  const handleOpenRecruiterParallelLoginDialog = (account: LinkedinAccount) => {
    return () => {
      setRecruiterParallelLoginDialog((prev) => ({ ...prev, open: true, account }));
    };
  };

  const handleCloseRecruiterParallelLoginDialog = () => {
    resetRecruiterParallelLoginDialog();
  };

  const handleReSynchronizeLinkedinAccountFromParallelLogin = () => {
    const { account } = recruiterParallelLoginDialog;

    if (!_.isNil(account)) {
      handleOpenAppSyncAndSynchronizeLiAccount(account)();
      handleCloseRecruiterParallelLoginDialog();
    }
  };

  /**
   * Onboarding incomplete dialog handlers
   */

  const handleOpenOnboardingIncompleteDialog = (account: LinkedinAccount) => {
    return () => {
      setOnboardingIncompleteDialog((prev) => ({ ...prev, open: true, account }));
    };
  };

  const handleCloseOnboardingIncompleteDialog = () => {
    resetOnboardingIncompleteDialog();
  };

  const handleReSynchronizeLinkedinAccountFromOnboardingIncomplete = () => {
    const { account } = onboardingIncompleteDialog;

    if (!_.isNil(account)) {
      handleOpenAppSyncAndSynchronizeLiAccount(account)();
      handleCloseOnboardingIncompleteDialog();
    }
  };

  /**
   * Fair use pause dialog handlers
   */

  const handleOpenFairUsePauseDialog = () => {
    setFairUsePauseDialog((prev) => ({ ...prev, open: true }));
  };

  const handleCloseFairUsePauseDialog = () => {
    setFairUsePauseDialog({ open: false });
  };

  /**
   * 2FA not enabled dialog
   */

  const handleOpenTwoFaNotEnabledDialog = (account: LinkedinAccount) => {
    return () => {
      setTwoFaNotEnabledDialog((prev) => ({ ...prev, open: true, account }));
    };
  };

  const handleCloseTwoFaNotEnabledDialog = () => {
    setTwoFaNotEnabledDialog({ open: false, account: null });
  };

  const handleReSynchronizeLinkedinAccountFromTwoFaNotEnabled = () => {
    const { account } = twoFaNotEnabledDialog;

    if (!_.isNil(account)) {
      handleOpenAppSyncAndSynchronizeLiAccount(account)();
      handleCloseTwoFaNotEnabledDialog();
    }
  };

  /**
   * Share token handlers
   */

  const copyPathToClipboard = async (path: string) => {
    try {
      await navigator.clipboard.writeText(path);
      setCopied(true);
    } catch (e) {
      // fallback for outlook in IFrame
      // for more information see here: https://web.dev/async-clipboard/#permissions-policy-integration
      Logger.error(e);
    }
  };

  const generateShareableCreationPath = async () => {
    setGenerateCreationLinkLoading(true);
    const res = await getShareableToken();

    if (!res.error) {
      const shareableToken = res.token;
      const dynamicRoute: string = `${window.location.origin}${path}?token=${shareableToken}`;
      copyPathToClipboard(dynamicRoute);
    } else {
      handleOpenErrorDialog(res?.error, '/shareable-token', 'get');
    }
    setGenerateCreationLinkLoading(false);
  };

  const generateShareableEditPath = (accountId: string) => {
    return async () => {
      const res = await getShareableToken();

      if (!res.error) {
        const shareableToken = res.token;
        const dynamicRoute: string = `${window.location.origin}${path}?token=${shareableToken}&id=${accountId}`;
        copyPathToClipboard(dynamicRoute);
      } else {
        handleOpenErrorDialog(res?.error, '/shareable-token', 'get');
      }
    };
  };

  const handleClose = (event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setCopied(false);
  };

  /**
   * Logout linkedin account handler
   */

  const startLogoutLoading = (accountId: string) => {
    setLogoutLoading((prev) => [...prev, accountId]);
  };

  const endLogoutLoading = (accountId: string) => {
    setLogoutLoading((prev) => prev.filter((id) => id !== accountId));
  };

  /**
   * Soft cancel of a stuck login — the row's "Abbrechen" while connecting.
   * POST /:id/reset stops the running job and resets the status to logged-out, but it does
   * NOT log the device out: the logout runs `pm clear`, which wipes the app's cookie jar and
   * with it the device trust, so every following login needs credentials + 2FA again for
   * each app (Toni Künzl 2026-09-08: one "Abbrechen" → six failed re-logins → lock).
   */
  const handleCancelLinkedinLogin = (account: LinkedinAccount) => {
    return async () => {
      if (_.isNil(account)) return;
      const accountId = account._id;

      startLogoutLoading(accountId);
      const res = await resetLinkedinAccount(accountId);
      endLogoutLoading(accountId);

      if (!res.error) {
        const resLinkedinAccount = await getLinkedinAccountById(accountId);
        if (!resLinkedinAccount.error) {
          setLinkedinAccounts((prevAccounts) =>
            prevAccounts.map((acc) => (acc._id === accountId ? { ...acc, status: resLinkedinAccount.status } : acc))
          );
        } else {
          handleOpenErrorDialog(resLinkedinAccount.error, `/linkedinAccounts/${accountId}`, 'get');
        }
      } else {
        handleOpenErrorDialog(res.error, `/linkedinAccounts/${accountId}/reset`, 'post');
      }
    };
  };

  const handleLogoutLinkedinAccount = (account: LinkedinAccount, stayLoggedOutTill?: number) => {
    return async () => {
      if (_.isNil(account)) return;
      const accountId = account._id;

      // Open the AppSyncDialog in logout mode so the user sees a progress spinner
      handleOpenAppSyncForLogout(account);
      startLogoutLoading(accountId);

      const res = await logoutLinkedinAccount(accountId, stayLoggedOutTill);

      endLogoutLoading(accountId);
      handleCloseAppSyncDialog();

      if (!res.error) {
        const resLinkedinAccount = await getLinkedinAccountById(accountId);
        if (!resLinkedinAccount.error) {
          setLinkedinAccounts((prevAccounts) =>
            prevAccounts.map((acc) => (acc._id === accountId ? { ...acc, status: resLinkedinAccount.status } : acc))
          );
        } else {
          handleOpenErrorDialog(resLinkedinAccount.error, `/linkedinAccounts/${accountId}`, 'get');
        }
      } else {
        handleOpenErrorDialog(res.error, `/linkedinAccounts/${accountId}/logout`, 'post');
      }
    };
  };

  /**
   * Kontaktliste (Verbindungen ersten Grades) eines Kontos
   */
  const handleOpenAccountConnectionsDialog = (account: LinkedinAccount) => {
    return () => {
      setAccountConnectionsDialog({ open: true, account });
    };
  };

  const handleCloseAccountConnectionsDialog = () => {
    resetAccountConnectionsDialog();
  };

  /**
   * Reset local storage handlers (NEW)
   */
  const handleOpenResetLocalStorageDialog = (account: LinkedinAccount) => {
    return () => {
      setResetLocalStorageDialog({ open: true, account, loading: false });
    };
  };

  const handleCloseResetLocalStorageDialog = () => {
    resetResetLocalStorageDialog();
  };

  const handleResetLocalStorage = async () => {
    if (_.isNil(resetLocalStorageDialog.account)) return;
    setResetLocalStorageDialog((prev) => ({ ...prev, loading: true }));
    const accountId = resetLocalStorageDialog.account._id;
    const res = await resetLinkedinAccountLocalStorage(accountId);
    if (!res.error) {
      fetchLinkedinAccounts(true);
      handleCloseResetLocalStorageDialog();
    } else {
      setResetLocalStorageDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(res.error, `/linkedinAccounts/${accountId}/resetLocalStorage`, 'post');
    }
  };

  /**
   * Edit signature dialog handlers
   */

  const handleOpenEditSignatureDialog = (account: LinkedinAccount) => () => {
    setEditSignatureDialog({
      open: true,
      account,
      signature: account.signature || '',
      loading: false,
    });
  };

  const handleCloseEditSignatureDialog = () => {
    resetEditSignatureDialog();
  };

  const handleSignatureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditSignatureDialog((prev) => ({
      ...prev,
      signature: event.target.value,
    }));
  };

  const handleSaveSignature = async () => {
    if (_.isNil(editSignatureDialog.account)) return;

    setEditSignatureDialog((prev) => ({
      ...prev,
      loading: true,
    }));

    const response = await updateLinkedinAccountSignature(editSignatureDialog.account._id, editSignatureDialog.signature);

    if (!response.error) {
      setLinkedinAccounts((prev) =>
        prev.map((account) => {
          if (account._id === editSignatureDialog.account?._id) {
            return {
              ...account,
              signature: editSignatureDialog.signature,
            };
          }
          return account;
        })
      );
      handleCloseEditSignatureDialog();
    } else {
      handleOpenErrorDialog(response.error, '/linkedinAccounts/:id', 'put');
      setEditSignatureDialog((prev) => ({
        ...prev,
        loading: false,
      }));
    }
  };

  const handleOpenManageEmailSendersDialog = (account: LinkedinAccount) => () => {
    setManageEmailSendersDialog({ open: true, account });
  };

  const handleCloseManageEmailSendersDialog = () => {
    setManageEmailSendersDialog({ open: false, account: null });
    fetchLinkedinAccounts(); // Refetch to update the list after changes
  };

  const handleEditLegacyEmailCredentials = (account: LinkedinAccount) => {
    handleCloseManageEmailSendersDialog();
    // open existing edit credentials dialog
    handleOpenEditEmailCredentialsDialog(account)();
  };

  const handleTimeZoneChange = (event: React.ChangeEvent<HTMLInputElement>, newValue: OptionType) => {
    setEditLinkedinAccountLimitsDialog((prev) => ({ ...prev, timeZone: newValue }));
  };

  // ----- local handlers for limits dialog -----
  const handleWorkDaysChange = (value: any /* FunnelDays */) => () => {
    setEditLinkedinAccountLimitsDialog((prev) => ({
      ...prev,
      workDays: prev.workDays.includes(value) ? prev.workDays.filter((d) => d !== value) : [...prev.workDays, value],
    }));
  };

  // Work windows: up to two per day, each 1-3h, together at most 5h.
  // Clicking a free hour adds it, clicking a selected hour cuts its window back
  // from there — see work-blocks.ts, which the API mirrors.
  const handleWorkHoursChange = (value: number) => () => {
    setEditLinkedinAccountLimitsDialog((prev) => ({
      ...prev,
      workHours: toggleWorkHour((prev.workHours || []).filter((h) => Number.isInteger(h)), value),
    }));
  };

  const handleConnectionsLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditLinkedinAccountLimitsDialog((prev) => ({ ...prev, connectionsLimit: Number(e.target.value) }));
  };
  const handleMessagesLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditLinkedinAccountLimitsDialog((prev) => ({ ...prev, messagesLimit: Number(e.target.value) }));
  };
  const handleEmailsLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditLinkedinAccountLimitsDialog((prev) => ({ ...prev, emailsLimit: Number(e.target.value) }));
  };
  const handleColdCallsLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditLinkedinAccountLimitsDialog((prev) => ({ ...prev, coldCallsLimit: Number(e.target.value) }));
  };
  const handleCooldownAfterMessagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditLinkedinAccountLimitsDialog((prev) => ({ ...prev, cooldownAfterMessages: Number(e.target.value) }));
  };
  const handleCooldownAfterVisitsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditLinkedinAccountLimitsDialog((prev) => ({ ...prev, cooldownAfterVisits: Number(e.target.value) }));
  };
  const handleVisitDailyLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditLinkedinAccountLimitsDialog((prev) => ({ ...prev, visitDailyLimit: Number(e.target.value) }));
  };
  const handleInmailDailyLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditLinkedinAccountLimitsDialog((prev) => ({ ...prev, inmailDailyLimit: Number(e.target.value) }));
  };

  // Quota split weights change handler
  const handleWeightChange = (funnelId: string, metric: keyof LimitWeights, value: number) => {
    setQuotaSplitDialog((prev) => ({
      ...prev,
      editedWeights: {
        ...prev.editedWeights,
        [funnelId]: {
          ...prev.editedWeights[funnelId],
          [metric]: value,
        },
      },
    }));
  };

  const handleRemoveLegacyEmailCredentials = async (account: LinkedinAccount) => {
    if (
      !window.confirm(
        t('delete') +
          ' ' +
          (account.emailConfig?.smtp?.username || account.emailConfig?.oauth?.email || account.emailConfig?.gmailOauth?.email || '') +
          '?'
      )
    )
      return;
    const res = await updateLinkedinAccountEmailCredentials(account._id, EmailAccountTypes.OTHER);
    if (!res.error) {
      fetchLinkedinAccounts(true);
      handleCloseManageEmailSendersDialog();
    } else {
      handleOpenErrorDialog(res.error, '/linkedinAccounts/:id/saveEmailConfig', 'post');
    }
  };

  /**
   * Edit linkedin account limits & schedule dialog handlers (NEW)
   */

  const handleOpenEditLinkedinAccountLimitsDialog = (account: LinkedinAccount) => {
    return () => {
      const isUnlicensed = !productLicensed;
      setEditLinkedinAccountLimitsDialog({
        ...editLinkedinAccountLimitsDialog,
        open: true,
        account,
        loading: false,
        workDays: account.workDays ?? [],
        // Accounts created before the two-window rule can carry more hours than
        // we now allow (the old default was 6h). Show the trimmed selection and
        // say so, rather than a picker the server would reject on save.
        workHours: normalizeWorkHours(account.workHours ?? []),
        workHoursTrimmedFrom:
          normalizeWorkHours(account.workHours ?? []).length < (account.workHours ?? []).length
            ? (account.workHours ?? []).length
            : null,
        timeZone: {
          label: (account.timeZone ?? account.timezone ?? 'Europe/Berlin') as string,
          value: (account.timeZone ?? account.timezone ?? 'Europe/Berlin') as string,
        },
        connectionsLimit: isUnlicensed ? 5 : account.connectionsLimit ?? (account as any).connectionDailyLimit ?? 20,
        messagesLimit: isUnlicensed ? 5 : account.messagesLimit ?? (account as any).messageDailyLimit ?? 100,
        emailsLimit: isUnlicensed ? 5 : account.emailsLimit ?? (account as any).emailDailyLimit ?? 30,
        coldCallsLimit: isUnlicensed ? 5 : account.coldCallsLimit ?? (account as any).coldCallDailyLimit ?? 20,
        cooldownAfterMessages: isUnlicensed ? 30 : account.cooldownAfterMessages ?? 30,
        cooldownAfterVisits: isUnlicensed ? 30 : account.cooldownAfterVisits ?? 30,
        visitDailyLimit: isUnlicensed ? 5 : account.visitDailyLimit ?? (account as any).visitDailyLimit ?? 500,
        inmailDailyLimit: isUnlicensed ? 5 : account.inmailDailyLimit ?? (account as any).inmailDailyLimit ?? 20,
      });
      // Fetch funnels & current quota weights
      setQuotaSplitDialog((prev) => ({
        ...prev,
        loading: true,
        open: true,
        accountLimits: isUnlicensed
          ? { connections: 5, messages: 5, emails: 5, coldCalls: 5, visits: 5, inMails: 5 }
          : {
              connections: account.connectionsLimit ?? (account as any).connectionDailyLimit ?? 0,
              messages: account.messagesLimit ?? (account as any).messageDailyLimit ?? 0,
              emails: account.emailsLimit ?? (account as any).emailDailyLimit ?? 0,
              coldCalls: account.coldCallsLimit ?? (account as any).coldCallDailyLimit ?? 0,
              visits: (account as any).visitDailyLimit ?? 0,
              inMails: account.inmailDailyLimit ?? (account as any).inmailDailyLimit ?? 0,
            },
      }));
      (async () => {
        const res = await getFunnelsOfLinkedinAccount(account._id);
        if (!res.error) {
          const funnels = (res.data as any[]) || [];
          // Initialize editedWeights with current campaign weights
          const initialWeights: Record<string, LimitWeights> = {};
          funnels.forEach((funnel) => {
            if (funnel.limitWeights) {
              initialWeights[funnel._id] = { ...funnel.limitWeights };
            }
          });
          setQuotaSplitDialog((prev) => ({
            ...prev,
            loading: false,
            funnels: funnels,
            editedWeights: initialWeights,
          }));
        } else {
          setQuotaSplitDialog((prev) => ({ ...prev, loading: false, funnels: [], editedWeights: {} }));
          handleOpenErrorDialog(res.error as any, '/linkedinAccounts/:id/linkedFunnels', 'get');
        }
      })();
    };
  };

  const handleCloseEditLinkedinAccountLimitsDialog = () => {
    resetQuotaSplitDialog();
    resetEditLinkedinAccountLimitsDialog();
  };

  const handleSaveLinkedinAccountLimits = async () => {
    if (!editLinkedinAccountLimitsDialog.account) return;

    setEditLinkedinAccountLimitsDialog((prev) => ({ ...prev, loading: true }));

    // split payloads for two endpoints
    const worktimePayload = {
      workDays: editLinkedinAccountLimitsDialog.workDays,
      workHours: [...editLinkedinAccountLimitsDialog.workHours].sort((a, b) => a - b),
      timeZone: editLinkedinAccountLimitsDialog.timeZone.value,
    };

    const limitsPayload = {
      messageDailyLimit: editLinkedinAccountLimitsDialog.messagesLimit,
      connectionDailyLimit: editLinkedinAccountLimitsDialog.connectionsLimit,
      emailDailyLimit: editLinkedinAccountLimitsDialog.emailsLimit,
      coldCallDailyLimit: editLinkedinAccountLimitsDialog.coldCallsLimit,
      connectionsLimit: editLinkedinAccountLimitsDialog.connectionsLimit,
      emailsLimit: editLinkedinAccountLimitsDialog.emailsLimit,
      coldCallsLimit: editLinkedinAccountLimitsDialog.coldCallsLimit,
      cooldownAfterMessages: editLinkedinAccountLimitsDialog.cooldownAfterMessages,
      cooldownAfterVisits: editLinkedinAccountLimitsDialog.cooldownAfterVisits,
      visitDailyLimit: editLinkedinAccountLimitsDialog.visitDailyLimit,
      inmailDailyLimit: editLinkedinAccountLimitsDialog.inmailDailyLimit,
    } as any;

    try {
      // Save weight changes only for funnels with modified weights
      const weightUpdatePromises = Object.entries(quotaSplitDialog.editedWeights)
        .filter(([funnelId, weights]) => {
          const originalFunnel = quotaSplitDialog.funnels.find((f: any) => f._id === funnelId);
          return !originalFunnel || !_.isEqual(weights, originalFunnel.limitWeights);
        })
        .map(([funnelId, weights]) => updateLinkedinFunnelLimitWeights(funnelId, { limitWeights: weights }));

      await Promise.all([
        patchLinkedinAccountWorktime(editLinkedinAccountLimitsDialog.account._id, worktimePayload as any),
        patchLinkedinAccountLimits(editLinkedinAccountLimitsDialog.account._id, limitsPayload),
        ...weightUpdatePromises,
      ]);
      fetchLinkedinAccounts(true);
      handleCloseEditLinkedinAccountLimitsDialog();
    } catch (error: any) {
      setEditLinkedinAccountLimitsDialog((prev) => ({ ...prev, loading: false }));
      handleOpenErrorDialog(error, '/linkedinAccounts/:id', 'patch');
    }
  };
  return (
    <>
      <div className={classes.container}>
        <div className={classes.tabContainer}>
          <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
            <Stack direction={'row'} alignItems={'center'} gap={2}>
              <Typography className={classes.text}>{`${t('activeAccounts')}: ${licenseCapacity.current}`}</Typography>
              <Typography className={classes.text}>|</Typography>
              <Typography className={classes.text}>{`${t('remainingAccounts')}: ${licenseCapacity.remaining}`}</Typography>
              {role === Roles.MANAGER && licenseCapacity.current > 0 && licenseCapacity.remaining === 0 ? (
                <PaiLink text={t('upgradeNow')} size={'lg'} variant={'primary'} underline={'always'} action={handleOpenUpgradePage} />
              ) : null}
            </Stack>
            <AddAccountButton
              handleOpenAppSyncDialog={handleOpenAppSyncDialog}
              generateShareableCreationPath={generateShareableCreationPath}
              disabled={
                licenseCapacity.remaining === 0 ||
                generateCreationLinkLoading
              }
              disabledReasonNoSeats={licenseCapacity.remaining === 0}
            />
          </Stack>
        </div>
        <div className={classes.content}>
          {linkedinAccountsLoading ? (
            <PaiDotLoader />
          ) : (
            <TableContainer className={classes.table}>
              <Table size={'small'} stickyHeader={true}>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: '16%' }}>{t('linkedinAccount')}</TableCell>
                    <TableCell style={{ width: '18%' }}>{t('linkedinEmail')}</TableCell>
                    <TableCell style={{ width: '14%' }}>{t('product')}</TableCell>
                    <TableCell style={{ width: '14%' }}>{t('license')}</TableCell>
                    <TableCell style={{ width: '18%' }}>{t('emailAccount')}</TableCell>
                    <TableCell style={{ width: '20%' }}>{t('accountStatus')}</TableCell>
                    <TableCell style={{ width: 'calc(28px + 16px)' }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!_.isEmpty(linkedinAccounts) ? (
                    linkedinAccounts.map((account) => (
                      <LinkedinAccountItem
                        key={account._id}
                        account={account}
                        linkedLicenses={linkedLicensesMap[account._id] || []}
                        syncLoading={appSyncDialog.loading.sync}
                        logoutLoading={logoutLoading.includes(account._id)}
                        handleOpenAppSyncAndSynchronizeLiAccount={handleOpenAppSyncAndSynchronizeLiAccount}
                        handleOpenAppSyncAndCorrectAccessData={handleOpenAppSyncAndCorrectAccessData}
                        handleOpenRecruiterParallelLoginDialog={handleOpenRecruiterParallelLoginDialog}
                        handleOpenRemoveLinkedinAccountDialog={handleOpenRemoveLinkedinAccountDialog}
                        handleOpenEditLinkedinAccountGeneralSettingsDialog={handleOpenEditLinkedinAccountGeneralSettingsDialog}
                        handleOpenEditLinkedinAccountLoginInformationDialog={handleOpenEditLinkedinAccountLoginInformationDialog}
                        handleOpenEditEmailCredentialsDialog={handleOpenEditEmailCredentialsDialog}
                        handleOpenManageEmailSendersDialog={handleOpenManageEmailSendersDialog}
                        handleOpenAppSyncAndVerifyPin={handleOpenAppSyncAndVerifyPin}
                        handleOpenAppSyncAndSolveCaptcha={handleOpenAppSyncAndSolveCaptcha}
                        handleOpenAppSyncThirdPartyWebsite={handleOpenAppSyncThirdPartyWebsite}
                        handleOpenEditLinkedinAccountNameDialog={handleOpenEditLinkedinAccountNameDialog}
                        handleLogoutLinkedinAccount={handleLogoutLinkedinAccount}
                        handleCancelLinkedinLogin={handleCancelLinkedinLogin}
                        generateShareableEditPath={generateShareableEditPath}
                        handleOpenOnboardingIncompleteDialog={handleOpenOnboardingIncompleteDialog}
                        handleOpenFairUsePauseDialog={handleOpenFairUsePauseDialog}
                        handleOpenTwoFaNotEnabledDialog={handleOpenTwoFaNotEnabledDialog}
                        handleOpenEditSignatureDialog={handleOpenEditSignatureDialog}
                        handleOpenEditLimitsDialog={handleOpenEditLinkedinAccountLimitsDialog}
                        handleOpenAssignLicenseDialog={(account) => () => setAssignLicenseDialog({ open: true, accountId: account._id })}
                        handleOpenResetLocalStorageDialog={handleOpenResetLocalStorageDialog}
                        handleOpenAccountConnectionsDialog={handleOpenAccountConnectionsDialog}
                      />
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} style={{ textAlign: 'center', borderBottom: 'none' }}>
                        {t('noAccountsAdded')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
      </div>

      <Snackbar
        className={classes.snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={copied}
        autoHideDuration={5000}
        onClose={handleClose}
        message={t('copiedLinkToClipboard')}
      />
      <AppSyncDialog
        open={appSyncDialog.open}
        product={productGroup}
        handleClose={handleCloseAppSyncDialog}
        isStarterExperience={appSyncDialog.isStarterExperience}
        isLogoutMode={appSyncDialog.isLogoutMode}
        activeStepIndex={appSyncDialog.activeStepIndex}
        loading={appSyncDialog.loading}
        syncProcessingStatus={appSyncDialog.syncProcessingStatus}
        syncError={appSyncDialog.error}
        selectedLinkedinProduct={appSyncDialog.selectedLinkedinProduct}
        allCountryCodes={allCountryCodes}
        selectedCountry={appSyncDialog.selectedCountry}
        selectedCity={appSyncDialog.selectedCity}
        handleLinkedinProductChange={handleAppSyncLinkedinProductChange}
        handleCountryChange={handleAppSyncCountryChange}
        handleCityChange={handleAppSyncCityChange}
        userName={appSyncDialog.userName}
        password={appSyncDialog.password}
        linkedinAccount={appSyncDialog.linkedinAccount}
        verificationCode={appSyncDialog.verificationCode}
        captcha={appSyncDialog.captcha}
        markerPosition={appSyncDialog.markerPosition}
        thirdPartyScreenshot={appSyncDialog.thirdPartyScreenshot}
        thirdPartyText={appSyncDialog.thirdPartyText}
        linkedinContracts={appSyncDialog.foundContractNames}
        selectedContract={appSyncDialog.selectedContract}
        increaseActiveStepIndex={increaseActiveStepIndex}
        decreaseActiveStepIndex={decreaseActiveStepIndex}
        handleUserNameChange={handleAppSyncUserNameChange}
        handlePasswordChange={handleAppSyncPasswordChange}
        moveToCampaignBuilder={moveToCampaignBuilder}
        handleCreateAndSynchronizeLiAccount={handleCreateAndSynchronizeLiAccount}
        handleVerificationCodeChange={handleAppSyncVerificationCodeChange}
        handleVerifyLinkedinAccountFromAppSync={handleVerifyLinkedinAccountFromAppSync}
        handleImageClick={handleImageClickFromAppSync}
        handleDeleteMarker={handleDeleteMarkerFromAppSync}
        handleSolveCaptcha={handleSolveCaptchaFromAppSync}
        handleThirdPartyClick={handleThirdPartyClickFromAppSync}
        handleThirdPartyTextChange={handleThirdPartyTextChange}
        handleSetThirdPartyTextFromAppSync={handleSetThirdPartyTextFromAppSync}
        handleSelectContract={handleSelectContractFromAppSync}
        handleAddContractFromAppSync={handleAddContractFromAppSync}
        handleEditAndSynchronizeLiAccount={handleEditAndSynchronizeLiAccount}
        handleReSynchronizeLiAccount={handleReSynchronizeLiAccount}
        onResetAccount={handleResetLinkedinAccount}
        handleOpenEditLinkedinAccountNameDialog={handleOpenEditLinkedinAccountNameDialog}
        selectedLicenseId={appSyncDialog.selectedLicenseId}
        handleLicenseSelect={handleAppSyncLicenseSelect}
      />
      <AccountAlreadyExistsDialog
        open={accountAlreadyExistsDialog.open}
        userEmail={accountAlreadyExistsDialog.userEmail}
        handleClose={handleCloseAccountAlreadyExistsDialog}
      />
      <WaitBeforeSyncDialog
        open={waitBeforeSyncDialog.open}
        waitingTime={waitBeforeSyncDialog.waitingTime}
        handleClose={handleCloseWaitBeforeSyncDialog}
      />
      <AccountAlreadySyncedDialog open={accountAlreadySyncedDialog.open} handleClose={handleCloseAccountAlreadySyncedDialog} />
      <RemoveLinkedinAccountDialog
        open={removeLinkedinAccountDialog.open}
        account={removeLinkedinAccountDialog.account}
        loading={removeLinkedinAccountDialog.loading}
        handleClose={handleCloseRemoveLinkedinAccountDialog}
        handleRemoveLinkedinAccount={handleRemoveLinkedinAccount}
      />
      <EditLinkedinAccountGeneralSettingsDialog
        open={editLinkedinAccountGeneralSettingsDialog.open}
        allCountryCodes={allCountryCodes}
        selectedCountry={editLinkedinAccountGeneralSettingsDialog.selectedCountry}
        selectedCity={editLinkedinAccountGeneralSettingsDialog.selectedCity}
        linkedinProduct={editLinkedinAccountGeneralSettingsDialog.linkedinProduct}
        loading={editLinkedinAccountGeneralSettingsDialog.loading}
        handleCountryChange={handleEditCountryChange}
        handleCityChange={handleEditCityChange}
        handleLinkedinProductChange={handleEditLinkedinProductChange}
        handleClose={handleCloseEditLinkedinAccountGeneralSettingsDialog}
        handleEditLinkedinAccount={handleEditLinkedinAccountGeneralSettings}
      />
      <EditLinkedinAccountLoginInformationDialog
        open={editLinkedinAccountLoginInformationDialog.open}
        userName={editLinkedinAccountLoginInformationDialog.userName}
        password={editLinkedinAccountLoginInformationDialog.password}
        loading={editLinkedinAccountLoginInformationDialog.loading}
        handleUserNameChange={handleEditUserNameChange}
        handlePasswordChange={handleEditPasswordChange}
        handleClose={handleCloseEditLinkedinAccountLoginInformationDialog}
        handleEditLinkedinAccount={handleEditLinkedinAccountLoginInformation}
      />
      <EditEmailCredentialsDialog
        open={editEmailCredentialsDialog.open}
        type={editEmailCredentialsDialog.type}
        smtp={editEmailCredentialsDialog.smtp}
        imap={editEmailCredentialsDialog.imap}
        oauth={editEmailCredentialsDialog.oauth}
        error={editEmailCredentialsDialog.error}
        accessRevoked={editEmailCredentialsDialog.accessRevoked}
        loading={editEmailCredentialsDialog.loading}
        handleEmailAccountTypeChange={handleEmailAccountTypeChange}
        handleSmtpEmailCredentialsChange={handleSmtpEmailCredentialsChange}
        handleSmtpEncryptionChange={handleSmtpEncryptionChange}
        handleImapEmailCredentialsChange={handleImapEmailCredentialsChange}
        handleImapEncryptionChange={handleImapEncryptionChange}
        handleOAuthEmailCredentialsChange={handleOAuthEmailCredentialsChange}
        handleClose={handleCloseEditEmailCredentialsDialog}
        handleAcceptEmailCredentials={handleAcceptEmailCredentials}
      />
      <EditLinkedinAccountNameDialog
        open={editLinkedinAccountNameDialog.open}
        loading={editLinkedinAccountNameDialog.loading}
        firstName={editLinkedinAccountNameDialog.firstName}
        lastName={editLinkedinAccountNameDialog.lastName}
        userEmail={editLinkedinAccountNameDialog.account?.userEmail}
        handleFirstNameChange={handleFirstNameChange}
        handleLastNameChange={handleLastNameChange}
        handleClose={handleCloseEditLinkedinAccountNameDialog}
        handleEditLinkedinAccountName={handleEditLinkedinAccountName}
      />
      <RecruiterParallelLoginDialog
        open={recruiterParallelLoginDialog.open}
        loading={recruiterParallelLoginDialog.loading}
        handleClose={handleCloseRecruiterParallelLoginDialog}
        handleSynchronizeLinkedinAccount={handleReSynchronizeLinkedinAccountFromParallelLogin}
      />
      <OnboardingIncompleteDialog
        open={onboardingIncompleteDialog.open}
        loading={onboardingIncompleteDialog.loading}
        handleClose={handleCloseOnboardingIncompleteDialog}
        handleSynchronizeLinkedinAccount={handleReSynchronizeLinkedinAccountFromOnboardingIncomplete}
      />
      <FairUsePauseDialog open={fairUsePauseDialog.open} handleClose={handleCloseFairUsePauseDialog} />
      <TwoFaNotEnabledDialog
        open={twoFaNotEnabledDialog.open}
        account={twoFaNotEnabledDialog.account}
        handleClose={handleCloseTwoFaNotEnabledDialog}
        handleSynchronizeLinkedinAccount={handleReSynchronizeLinkedinAccountFromTwoFaNotEnabled}
      />
      <AccountConnectionsDialog
        open={accountConnectionsDialog.open}
        account={accountConnectionsDialog.account}
        handleClose={handleCloseAccountConnectionsDialog}
      />
      <ResetLocalStorageDialog
        open={resetLocalStorageDialog.open}
        account={resetLocalStorageDialog.account}
        loading={resetLocalStorageDialog.loading}
        handleClose={handleCloseResetLocalStorageDialog}
        handleResetLocalStorage={handleResetLocalStorage}
      />
      <EditSignatureDialog
        open={editSignatureDialog.open}
        account={editSignatureDialog.account}
        signature={editSignatureDialog.signature}
        loading={editSignatureDialog.loading}
        handleSignatureChange={handleSignatureChange}
        handleClose={handleCloseEditSignatureDialog}
        handleSaveSignature={handleSaveSignature}
      />
      <ManageEmailSendersDialog
        open={manageEmailSendersDialog.open}
        token={token}
        account={manageEmailSendersDialog.account}
        onClose={handleCloseManageEmailSendersDialog}
        onEditLegacy={handleEditLegacyEmailCredentials}
        onRemoveLegacy={handleRemoveLegacyEmailCredentials}
      />
      <EditAccountLimitsAndWeightsDialog
        open={editLinkedinAccountLimitsDialog.open}
        loading={editLinkedinAccountLimitsDialog.loading}
        timeZone={editLinkedinAccountLimitsDialog.timeZone}
        workDays={editLinkedinAccountLimitsDialog.workDays as any}
        workHours={editLinkedinAccountLimitsDialog.workHours}
        workHoursTrimmedFrom={editLinkedinAccountLimitsDialog.workHoursTrimmedFrom}
        connectionsLimit={editLinkedinAccountLimitsDialog.connectionsLimit}
        messagesLimit={editLinkedinAccountLimitsDialog.messagesLimit}
        emailsLimit={editLinkedinAccountLimitsDialog.emailsLimit}
        coldCallsLimit={editLinkedinAccountLimitsDialog.coldCallsLimit}
        cooldownAfterMessages={editLinkedinAccountLimitsDialog.cooldownAfterMessages}
        cooldownAfterVisits={editLinkedinAccountLimitsDialog.cooldownAfterVisits}
        visitDailyLimit={editLinkedinAccountLimitsDialog.visitDailyLimit}
        inmailDailyLimit={editLinkedinAccountLimitsDialog.inmailDailyLimit}
        handleTimeZoneChange={handleTimeZoneChange}
        handleWorkDaysChange={handleWorkDaysChange}
        handleWorkHoursChange={handleWorkHoursChange}
        handleConnectionsLimitChange={handleConnectionsLimitChange}
        handleMessagesLimitChange={handleMessagesLimitChange}
        handleEmailsLimitChange={handleEmailsLimitChange}
        handleColdCallsLimitChange={handleColdCallsLimitChange}
        handleCooldownAfterMessagesChange={handleCooldownAfterMessagesChange}
        handleCooldownAfterVisitsChange={handleCooldownAfterVisitsChange}
        handleVisitDailyLimitChange={handleVisitDailyLimitChange}
        handleInmailDailyLimitChange={handleInmailDailyLimitChange}
        funnels={quotaSplitDialog.funnels}
        accountLimits={quotaSplitDialog.accountLimits}
        editedWeights={quotaSplitDialog.editedWeights}
        onWeightChange={handleWeightChange}
        handleClose={handleCloseEditLinkedinAccountLimitsDialog}
        handleSave={handleSaveLinkedinAccountLimits}
        readOnly={isUnlicensed}
      />
      <AssignLicenseDialog
        open={assignLicenseDialog.open}
        accountId={assignLicenseDialog.accountId}
        linkedLicenses={linkedLicensesMap[assignLicenseDialog.accountId] || []}
        onClose={() => setAssignLicenseDialog({ open: false, accountId: '' })}
        onSaved={() => fetchLinkedinAccounts(true)}
      />
      <CustomerOnboarding hasLinkedinAccounts={!_.isEmpty(linkedinAccounts)} handleOpenAddLinkedinAccountDialog={handleOpenAppSyncDialog} />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    background: theme.palette.backgrounds.main_new,
    height: 'calc(100vh - 60px - 32px)',
    borderRadius: '6px',
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.down('xl')]: {
      height: `calc((100vh / ${ZoomLevels.XL}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1300)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('lg')]: {
      height: `calc((100vh / ${ZoomLevels.LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1000)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_MD}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('md')]: {
      height: `calc((100vh / ${ZoomLevels.MD}) - 60px - 32px)`,
    },
  },
  tabContainer: {
    padding: '16px 32px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  content: {
    height: '100%',
    padding: '16px 32px',
    overflow: 'auto',
    display: 'flex',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'nowrap',
  },
  table: {
    borderRadius: '6px',
    '& th, & td': {
      fontSize: theme.typography.pxToRem(15),
      padding: '8px 16px',
      backgroundColor: '#FFF',
    },
    '& tr:last-child td': {
      borderBottom: 'none',
    },
    '& tr td:nth-last-of-type(-n+2)': {
      borderLeft: '1px solid #ddd',
    },
    '& tr td:last-child': {
      padding: '8px',
    },
  },
  snackbar: {
    width: 'fit-content',
    '& .MuiPaper-root': {
      minWidth: 'unset',
      background: theme.palette.primary.main,
    },
    '& .MuiSnackbarContent-message': {
      padding: 0,
      fontSize: theme.typography.pxToRem(15),
    },
  },
}));

export default LinkedinAccountOverview;
